import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiAutocomplete from "components/AutoComplete";
import OverlayLoader from "components/OverlayLoader";

import useCommon from "hooks/useCommon";
import { FaExclamationCircle } from "react-icons/fa";
import { resetMaintenanceTaskState } from "store/slice/maintenanceSlice";
import { getUsers } from "store/slice/userSlice";
import { MAINTENANCE_TASK_CATEGORY_OPTIONS } from "variables";
import { MAINTENANCE_TASK_PRIORITY_OPTIONS } from "variables";
import { MAINTENANCE_TASK_STATUS_OPTIONS } from "variables";

const INITIAL_FORM = {
  title: "",
  description: "",
  category: null,
  roomArea: "",
  priority: MAINTENANCE_TASK_PRIORITY_OPTIONS[1],
  status: MAINTENANCE_TASK_STATUS_OPTIONS[0],
  assignedTo: [],

  recurringFrequency: null,
  scheduledDate: "",
  estimatedCost: "",
  notes: "",
};

const MaintenanceTaskDialog = ({ open, onClose, onSave, task = null, mode = "create" }) => {
  const { DISPATCH, STORE } = useCommon();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const {
    createTaskLoading,
    createTaskError,
    updateTaskLoading,
    selectedTaskLoading,
    updateTaskError,
  } = STORE((state) => state.maintenance);

  const { list: users, listLoading: usersLoading } = STORE((state) => state.user);

  const hotelId = STORE((state) => state.ui.hotelId);

  const isSaving = mode === "edit" ? updateTaskLoading : createTaskLoading;

  const apiError = mode === "edit" ? updateTaskError : createTaskError;

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.title?.trim()) {
      newErrors.title = "Task title is required";
    }

    if (!form.description?.trim()) {
      newErrors.description = "Description is required";
    }

    if (!form.category) {
      newErrors.category = "Category is required";
    }

    if (!form.roomArea?.trim()) {
      newErrors.roomArea = "Room / Area is required";
    }

    if (form.estimatedCost !== "" && Number(form.estimatedCost) < 0) {
      newErrors.estimatedCost = "Estimated repair cost cannot be negative";
    }

    if (!form.scheduledDate) {
      newErrors.scheduledDate = "Scheduled date is required";
    }

    if (form.schedulingType?.value === "recurring" && !form.recurringFrequency) {
      newErrors.recurringFrequency = "Recurring frequency is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      title: form.title,
      description: form.description,
      category: form.category?.value,
      categoryLabel: form.category?.label,
      roomArea: form.roomArea,
      priority: form.priority?.value,
      priorityLabel: form.priority?.label,
      status: form.status?.value,
      statusLabel: form.status?.label,
      assignedToUserIds: form.assignedTo,
      scheduledDate: form.scheduledDate,
      estimatedCost: Number(form.estimatedCost || 0),
      notes: form.notes,
    };

    onSave?.(payload);
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;

    if (mode === "create") {
      setForm({ ...INITIAL_FORM });
      return;
    }

    if (mode === "edit" && task) {
      setForm({
        title: task.taskTitle || "",

        description: task.description || "",

        category:
          MAINTENANCE_TASK_CATEGORY_OPTIONS.find((item) => item.label === task.category) || null,

        roomArea: task.roomArea || "",

        priority:
          MAINTENANCE_TASK_PRIORITY_OPTIONS.find((item) => item.label === task.priority) ||
          MAINTENANCE_TASK_PRIORITY_OPTIONS[1],

        status:
          MAINTENANCE_TASK_STATUS_OPTIONS.find(
            (item) => item.label.replace(/\s/g, "") === task.status.replace(/\s/g, "")
          ) || MAINTENANCE_TASK_STATUS_OPTIONS[0],

        assignedTo: Array.isArray(task.assignedStaff)
          ? task.assignedStaff.map((staff) => staff.id || staff._id).filter(Boolean)
          : task.assignedStaff?.id || task.assignedStaff?._id
            ? [task.assignedStaff.id || task.assignedStaff._id]
            : task.assignedStaffIds || [],

        recurringFrequency: null,

        scheduledDate: task.scheduledDate ? task.scheduledDate.split("T")[0] : "",

        estimatedCost: task.estimatedRepairCost || "",

        notes: "",
      });
    } else {
      setForm(INITIAL_FORM);
    }
  }, [open, task]);

  useEffect(() => {
    if (!open) return;

    DISPATCH(resetMaintenanceTaskState());
    DISPATCH(getUsers({ hotelId, isOnlyActive: true, includeMe: true, includeAdmin: true }));

    setErrors({});
  }, [open, DISPATCH]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
    >
      <DialogTitle>
        <VuiTypography variant="h5">
          {mode === "edit" ? "Edit Maintenance Task" : "Create Maintenance Task"}
        </VuiTypography>
      </DialogTitle>

      <DialogContent>
        <OverlayLoader
          open={selectedTaskLoading || isSaving}
          message={
            selectedTaskLoading
              ? "Loading task details..."
              : mode === "edit"
                ? "Updating task..."
                : "Creating task..."
          }
        />

        <VuiBox display="flex" flexDirection="column" gap={0} mt={0}>
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Task Title*
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              inputProps={{ maxLength: 30 }}
            />

            {errors.title && (
              <VuiTypography variant="caption" color="error">
                {errors.title}
              </VuiTypography>
            )}
          </VuiBox>

          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Description*
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              inputProps={{ maxLength: 100 }}
            />

            {errors.description && (
              <VuiTypography variant="caption" color="error">
                {errors.description}
              </VuiTypography>
            )}
          </VuiBox>

          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Category*
            </VuiTypography>

            <VuiAutocomplete
              options={MAINTENANCE_TASK_CATEGORY_OPTIONS}
              value={form.category}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              placeholder="Select category"
              gradient
              onChange={(_, selected) => handleChange("category", selected)}
            />

            {errors.category && (
              <VuiTypography variant="caption" color="error">
                {errors.category}
              </VuiTypography>
            )}
          </VuiBox>

          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Room / Area*
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              value={form.roomArea}
              onChange={(e) => handleChange("roomArea", e.target.value)}
            />

            {errors.roomArea && (
              <VuiTypography variant="caption" color="error">
                {errors.roomArea}
              </VuiTypography>
            )}
          </VuiBox>

          <VuiBox display="flex" gap={1}>
            <VuiBox flex={1}>
              <VuiTypography variant="caption" ml={1}>
                Priority
              </VuiTypography>

              <VuiAutocomplete
                options={MAINTENANCE_TASK_PRIORITY_OPTIONS}
                value={form.priority}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(o, v) => o?.value === v?.value}
                gradient
                onChange={(_, selected) => handleChange("priority", selected)}
              />
            </VuiBox>

            <VuiBox flex={1}>
              <VuiTypography variant="caption" ml={1}>
                Status
              </VuiTypography>

              <VuiAutocomplete
                options={MAINTENANCE_TASK_STATUS_OPTIONS}
                value={form.status}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(o, v) => o?.value === v?.value}
                gradient
                onChange={(_, selected) => handleChange("status", selected)}
              />
            </VuiBox>
          </VuiBox>

          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Assign To
            </VuiTypography>

            <VuiAutocomplete
              multiple
              options={users || []}
              value={(users || []).filter((u) => form.assignedTo.includes(u._id || u.id))}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              placeholder="Assign users"
              gradient
              loading={usersLoading}
              onChange={(_, selected) => {
                handleChange(
                  "assignedTo",
                  selected.map((u) => u._id)
                );
              }}
            />
          </VuiBox>

          <Divider sx={{ my: 1 }} />

          {/* =========================
              COST AND BUDGET
          ========================= */}

          <VuiTypography variant="button" fontWeight="bold">
            Budget & Scheduling
          </VuiTypography>

          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Estimated Repair Cost
            </VuiTypography>

            <VuiInput
              type="number"
              gradient
              fullWidth
              value={form.estimatedCost}
              inputProps={{
                min: 0,
              }}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "" || Number(value) >= 0) {
                  handleChange("estimatedCost", value);
                }
              }}
            />
            {errors.estimatedCost && (
              <VuiTypography variant="caption" color="error">
                {errors.estimatedCost}
              </VuiTypography>
            )}
          </VuiBox>

          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Scheduled Date*
            </VuiTypography>

            <VuiInput
              type="date"
              gradient
              fullWidth
              value={form.scheduledDate}
              onChange={(e) => handleChange("scheduledDate", e.target.value)}
            />

            {errors.scheduledDate && (
              <VuiTypography variant="caption" color="error">
                {errors.scheduledDate}
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>
      </DialogContent>

      <VuiBox px={3} pb={1}>
        {apiError && (
          <VuiBox
            display="flex"
            alignItems="center"
            gap={1}
            p={1.2}
            mt={2}
            borderRadius="md"
            sx={{
              border: "1px solid rgba(239,68,68,0.4)",
              background: "rgba(239,68,68,0.08)",
            }}
          >
            <FaExclamationCircle size={14} color="#ef4444" />

            <VuiTypography variant="caption" color="error">
              {typeof apiError === "string"
                ? apiError
                : apiError?.message ||
                  (mode === "edit"
                    ? "Failed to update maintenance task"
                    : "Failed to create maintenance task")}
            </VuiTypography>
          </VuiBox>
        )}
      </VuiBox>

      <DialogActions>
        <VuiButton
          variant="outlined"
          color="primary"
          onClick={handleClose}
          disabled={selectedTaskLoading || isSaving}
        >
          Cancel
        </VuiButton>

        <VuiButton
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={selectedTaskLoading || isSaving}
        >
          {createTaskLoading ? "Creating..." : mode === "edit" ? "Save Changes" : "Create Task"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceTaskDialog;
